import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Badge, ProgressBar, ListGroup } from "react-bootstrap";
import { TrendingUp, Database, Activity, MapPin, Calendar, Droplets, CheckCircle, Target, AlertTriangle, Info } from "lucide-react";
import axios from 'axios';
import "./glass-admin.css"; // adjust path if needed

const AnalysisComponent = () => {
    const [apiEndpoint] = useState('https://makasaserver-0c12c0cf2c89.herokuapp.com/lisa_survey_irrigation/get_all_filter_with_grok?fbclid=IwY2xjawLYBulleHRuA2FlbQIxMABicmlkETFFWFpzbVNQazRzNGMxYVZLAR4hPHIiU6qb0OksEeIPVCgssvKx62qi42MSFfiIK9uOhv47msjlryweFR2nvQ_aem_3w6kWz3YYt4yHVGI8SP1dg');
    const [analysisData, setAnalysisData] = useState(null);
    const [loading, setLoading] = useState(false);

    // Auto-fetch data when component mounts with default endpoint
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get(apiEndpoint);
                setAnalysisData(response.data);
            } catch (err) {
                console.error('Failed to fetch data:', err);
                setAnalysisData(null);
            } finally {
                setLoading(false);
            }
        };

        if (apiEndpoint) {
            fetchData();
        }
    }, [apiEndpoint]); // Re-run if `apiEndpoint` changes

    // Process analysis data for irrigation status
    const processIrrigationData = (data) => {
        console.log('Processing data:', data); // Debug log
        
        // Check if we have a grok_summary (pre-analyzed data)
        if (data && data.grok_summary) {
            console.log('Found grok_summary:', data.grok_summary);
            return {
                hasPreAnalysis: true,
                summary: data.grok_summary,
                status: data.status
            };
        }
        
        // Handle raw field data (original logic)
        let actualData = data;
        if (data && typeof data === 'object') {
            // If data is an object with an array property, try to find the array
            if (data.data && Array.isArray(data.data)) {
                actualData = data.data;
            } else if (data.results && Array.isArray(data.results)) {
                actualData = data.results;
            } else if (data.items && Array.isArray(data.items)) {
                actualData = data.items;
            } else if (!Array.isArray(data)) {
                // If it's an object but not an array, try to find any array property
                const arrayProps = Object.keys(data).filter(key => Array.isArray(data[key]));
                if (arrayProps.length > 0) {
                    actualData = data[arrayProps[0]];
                    console.log(`Found array in property: ${arrayProps[0]}`);
                }
            }
        }
        
        if (!actualData || !Array.isArray(actualData)) {
            console.log('Data is null or not an array:', actualData);
            return null;
        }
        
        const totalFields = actualData.length;
        console.log('Total fields:', totalFields); // Debug log
        console.log('Sample field:', actualData[0]); // Debug log
        
        const wetFields = actualData.filter(field => field.irrigation_status === 'Wet').length;
        const dryFields = actualData.filter(field => field.irrigation_status === 'Dry').length;
        const wetPercentage = totalFields > 0 ? Math.round((wetFields / totalFields) * 100) : 0;
        const dryPercentage = totalFields > 0 ? Math.round((dryFields / totalFields) * 100) : 0;

        // Group fields by location/coordinates for clustering analysis
        const locationGroups = {};
        actualData.forEach(field => {
            if (field.latitude && field.longitude) {
                const key = `${Math.round(field.latitude * 1000)}_${Math.round(field.longitude * 1000)}`;
                if (!locationGroups[key]) {
                    locationGroups[key] = [];
                }
                locationGroups[key].push(field);
            }
        });

        const result = {
            totalFields,
            wetFields,
            dryFields,
            wetPercentage,
            dryPercentage,
            locationGroups: Object.keys(locationGroups).length,
            averageLatitude: actualData.reduce((sum, field) => sum + (field.latitude || 0), 0) / totalFields,
            averageLongitude: actualData.reduce((sum, field) => sum + (field.longitude || 0), 0) / totalFields,
            rawData: actualData
        };
        
        console.log('Analysis result:', result); // Debug log
        return result;
    };

    const analysisResult = processIrrigationData(analysisData);
    
    console.log('analysisData:', analysisData); // Debug log
    console.log('analysisResult:', analysisResult); // Debug log

    return (
        <Container fluid className="min-vh-100 glass-admin-bg">
            {/* Header */}
            <Row className="pt-4 pb-3">
                <Col>
                    <Card className="border-0 shadow-sm glass-surface">
                        <Card.Body className="py-3">
                            <Row className="align-items-center">
                                <Col xs={12} md={6}>
                                    <div className="d-flex align-items-center">
                                        <TrendingUp size={24} className="me-2 text-primary" />
                                        <span className="fs-5 fw-medium text-muted">
                                            Data Analysis Dashboard
                                        </span>
                                    </div>
                                </Col>
                                <Col xs={12} md={6} className="text-md-end mt-3 mt-md-0">
                                    <div className="d-flex justify-content-md-end justify-content-start align-items-center flex-wrap gap-2">
                                        <Badge bg="light" text="dark" className="px-2 py-1">
                                            <MapPin size={14} className="me-1" />
                                            Analysis
                                        </Badge>
                                        <Badge bg="light" text="dark" className="px-2 py-1">
                                            <Calendar size={14} className="me-1" />
                                            Real-time
                                        </Badge>
                                        <Badge bg="light" text="dark" className="px-2 py-1">
                                            <Activity size={14} className="me-1" />
                                            Dynamic
                                        </Badge>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Main Content */}
            <Row className="pb-4">
                <Col>
                    <Card className="border-0 shadow-sm glass-surface">
                        <Card.Body className="p-4">
                            {/* Analysis Results Section */}
                            <Row>
                                <Col>
                                    {loading ? (
                                        <Card className="border-0 glass-surface glass-surface--thin">
                                            <Card.Body>
                                                <div className="text-center py-5">
                                                    <div className="spinner-border text-primary mb-3" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                    <p className="text-muted mb-0">Loading irrigation analysis data...</p>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    ) : analysisResult && analysisResult.hasPreAnalysis ? (
                                        /* Pre-analyzed Summary from API */
                                        <Card className="border-0 glass-surface glass-surface--thin">
                                            <Card.Body>
                                                <div className="d-flex align-items-center mb-3">
                                                    <TrendingUp size={24} className="me-2 text-primary" />
                                                    <h4 className="mb-0 fw-bold">Irrigation Analysis Report</h4>
                                                </div>
                                                
                                                <div className="glass-paper glass-paper--lg">
                                                    <div 
                                                        style={{
                                                            whiteSpace: 'pre-wrap',
                                                            fontFamily: "'Poppins', sans-serif",
                                                            fontSize: '15px'
                                                        }}
                                                        dangerouslySetInnerHTML={{
                                                            __html: analysisResult.summary
                                                                .replace(/###/g, '<h3 style="color: #059669; margin-top: 20px; margin-bottom: 15px; font-weight: normal;">')
                                                                .replace(/\*\*(.*?)\*\*/g, '<strong style="color: #1a202c;">$1</strong>')
                                                                .replace(/\*(.*?)\*/g, '<em>$1</em>')
                                                                .replace(/\n\n/g, '</p><p style="margin-bottom: 15px;">')
                                                                .replace(/^/, '<p style="margin-bottom: 15px;">')
                                                                .replace(/$/, '</p>')
                                                                .replace(/(\d+\.)/g, '<span style="color: #059669; font-weight: normal;">$1</span>')
                                                        }}
                                                    />
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    ) : analysisResult ? (
                                        <>
                                            {/* Overall Analysis Header */}
                                            <Card className="border-0 mb-4 glass-surface glass-surface--thin">
                                                <Card.Body>
                                                    <div className="d-flex align-items-center mb-3">
                                                        <TrendingUp size={24} className="me-2 text-primary" />
                                                        <h4 className="mb-0 fw-bold">Overall Analysis of Irrigation Status</h4>
                                                    </div>
                                                    
                                                    {/* Irrigation Status Summary */}
                                                    <div className="mb-4">
                                                        <h6 className="fw-bold mb-3">
                                                            <CheckCircle size={18} className="me-2 text-success" />
                                                            Irrigation Status Summary:
                                                        </h6>
                                                        <p className="text-muted mb-3">
                                                            Of the <strong>{analysisResult.totalFields} fields surveyed</strong>, {" "}
                                                            <span className="text-success fw-bold">{analysisResult.wetFields} fields ({analysisResult.wetPercentage}%)</span> are reported as "Wet," indicating adequate moisture, while {" "}
                                                            <span className="text-warning fw-bold">{analysisResult.dryFields} fields ({analysisResult.dryPercentage}%)</span> are "Dry," suggesting a need for irrigation or water intervention. 
                                                            This distribution highlights a generally well-irrigated area with localized dry patches.
                                                        </p>
                                                        
                                                        {/* Progress Bars */}
                                                        <Row className="mb-3">
                                                            <Col md={6}>
                                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                                    <small className="fw-medium text-success">Wet Fields</small>
                                                                    <small className="fw-bold text-success">{analysisResult.wetPercentage}%</small>
                                                                </div>
                                                                <ProgressBar 
                                                                    variant="success" 
                                                                    now={analysisResult.wetPercentage} 
                                                                    style={{height: '8px'}}
                                                                />
                                                            </Col>
                                                            <Col md={6}>
                                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                                    <small className="fw-medium text-warning">Dry Fields</small>
                                                                    <small className="fw-bold text-warning">{analysisResult.dryPercentage}%</small>
                                                                </div>
                                                                <ProgressBar 
                                                                    variant="warning" 
                                                                    now={analysisResult.dryPercentage} 
                                                                    style={{height: '8px'}}
                                                                />
                                                            </Col>
                                                        </Row>
                                                    </div>

                                                    {/* Spatial Patterns */}
                                                    <div className="mb-4">
                                                        <h6 className="fw-bold mb-3">
                                                            <MapPin size={18} className="me-2 text-primary" />
                                                            Spatial Patterns and Clustering:
                                                        </h6>
                                                        <ListGroup variant="flush">
                                                            <ListGroup.Item className="border-0 px-0">
                                                                <div className="d-flex align-items-start">
                                                                    <Droplets size={16} className="me-2 mt-1 text-primary" />
                                                                    <div>
                                                                        <strong>Wet Fields Clustering:</strong> Wet fields are predominantly clustered in specific zones, particularly around GPS coordinates near ({analysisResult.averageLatitude.toFixed(3)}, {analysisResult.averageLongitude.toFixed(3)}), areas which may indicate favorable topographic conditions for water retention.
                                                                    </div>
                                                                </div>
                                                            </ListGroup.Item>
                                                            <ListGroup.Item className="border-0 px-0">
                                                                <div className="d-flex align-items-start">
                                                                    <AlertTriangle size={16} className="me-2 mt-1 text-warning" />
                                                                    <div>
                                                                        <strong>Dry Fields Clustering:</strong> Dry fields are also spatially grouped, notably around specific micro-regions. This suggests localized water scarcity or irrigation system gaps in these areas.
                                                                    </div>
                                                                </div>
                                                            </ListGroup.Item>
                                                            <ListGroup.Item className="border-0 px-0">
                                                                <div className="d-flex align-items-start">
                                                                    <Target size={16} className="me-2 mt-1 text-info" />
                                                                    <div>
                                                                        <strong>Mixed Status:</strong> Some areas show a mix of wet and dry fields, indicating inconsistent water distribution or varying soil/water retention capabilities.
                                                                    </div>
                                                                </div>
                                                            </ListGroup.Item>
                                                        </ListGroup>
                                                    </div>

                                                    {/* Implications */}
                                                    <div className="mb-4">
                                                        <h6 className="fw-bold mb-3">
                                                            <Info size={18} className="me-2 text-info" />
                                                            Implications for Water Resource Management:
                                                        </h6>
                                                        <ListGroup variant="flush">
                                                            <ListGroup.Item className="border-0 px-0">
                                                                <div className="d-flex align-items-start">
                                                                    <span className="badge bg-warning me-2 mt-1">1</span>
                                                                    <div>
                                                                        <strong>The high proportion of wet fields suggests that overall water availability is sufficient,</strong> but distribution is uneven, leading to dry pockets. Over-irrigation in wet areas could be a concern, potentially causing waterlogging or nutrient leaching.
                                                                    </div>
                                                                </div>
                                                            </ListGroup.Item>
                                                            <ListGroup.Item className="border-0 px-0">
                                                                <div className="d-flex align-items-start">
                                                                    <span className="badge bg-warning me-2 mt-1">2</span>
                                                                    <div>
                                                                        <strong>Dry clusters point to infrastructure deficiencies</strong> (e.g., broken or missing irrigation channels) or environmental factors (e.g., soil type, elevation) that hinder water access in specific locations.
                                                                    </div>
                                                                </div>
                                                            </ListGroup.Item>
                                                            <ListGroup.Item className="border-0 px-0">
                                                                <div className="d-flex align-items-start">
                                                                    <span className="badge bg-warning me-2 mt-1">3</span>
                                                                    <div>
                                                                        <strong>Repeated GPS coordinates for multiple fields</strong> may indicate either data collection overlaps or small, adjacent plots with similar conditions, which could amplify the impact of localized dryness if not addressed.
                                                                    </div>
                                                                </div>
                                                            </ListGroup.Item>
                                                        </ListGroup>
                                                    </div>

                                                    {/* Recommendations */}
                                                    <div>
                                                        <h6 className="fw-bold mb-3">
                                                            <Target size={18} className="me-2 text-success" />
                                                            Recommendations for Irrigation Strategies:
                                                        </h6>
                                                        <ListGroup variant="flush">
                                                            <ListGroup.Item className="border-0 px-0 py-2">
                                                                <div className="d-flex align-items-start">
                                                                    <span className="badge bg-primary me-2 mt-1">1</span>
                                                                    <div>
                                                                        <strong>Targeted Irrigation for Dry Clusters:</strong> Prioritize irrigation interventions in dry zones, particularly around identified GPS coordinates. Deploy mobile or drip irrigation systems to address immediate needs without overwatering adjacent wet areas.
                                                                    </div>
                                                                </div>
                                                            </ListGroup.Item>
                                                            <ListGroup.Item className="border-0 px-0 py-2">
                                                                <div className="d-flex align-items-start">
                                                                    <span className="badge bg-primary me-2 mt-1">2</span>
                                                                    <div>
                                                                        <strong>Infrastructure Assessment:</strong> Investigate irrigation infrastructure in wet delivery systems for blockages, leaks, or coverage gaps. Consider extending or repairing systems to ensure equitable water distribution.
                                                                    </div>
                                                                </div>
                                                            </ListGroup.Item>
                                                            <ListGroup.Item className="border-0 px-0 py-2">
                                                                <div className="d-flex align-items-start">
                                                                    <span className="badge bg-primary me-2 mt-1">3</span>
                                                                    <div>
                                                                        <strong>Water Conservation in Wet Zones:</strong> Reduce water delivery systems efficiently in wet delivery systems (e.g., near GPS coordinates) to prevent waterlogging and optimize resource use. Implement soil moisture sensors to monitor and adjust watering schedules.
                                                                    </div>
                                                                </div>
                                                            </ListGroup.Item>
                                                            <ListGroup.Item className="border-0 px-0 py-2">
                                                                <div className="d-flex align-items-start">
                                                                    <span className="badge bg-primary me-2 mt-1">4</span>
                                                                    <div>
                                                                        <strong>Spatial Mapping and Monitoring:</strong> Use GPS data to create detailed irrigation maps for ongoing monitoring. This can help identify persistent dry/wet patterns and guide long-term planning, such as adjusting crop selection or irrigation layouts based on spatial trends.
                                                                    </div>
                                                                </div>
                                                            </ListGroup.Item>
                                                            <ListGroup.Item className="border-0 px-0 py-2">
                                                                <div className="d-flex align-items-start">
                                                                    <span className="badge bg-primary me-2 mt-1">5</span>
                                                                    <div>
                                                                        <strong>Soil and Topography Analysis:</strong> Conduct soil tests and topographic surveys in mixed-status areas to understand underlying causes of variability (e.g., drainage issues, soil porosity) and tailor solutions accordingly.
                                                                    </div>
                                                                </div>
                                                            </ListGroup.Item>
                                                        </ListGroup>
                                                    </div>

                                                    <div className="mt-4 p-3 glass-surface glass-surface--thin rounded">
                                                        <p className="mb-0 text-muted">
                                                            <strong>By focusing on precision irrigation and addressing localized disparities, water resource efficiency can be improved, ensuring sustainable agricultural productivity across the surveyed area.</strong>
                                                        </p>
                                                    </div>
                                                </Card.Body>
                                            </Card>

                                            {/* Raw Data Toggle */}
                                            <Card className="border-0 glass-surface glass-surface--thin">
                                                <Card.Body>
                                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                                        <div className="d-flex align-items-center">
                                                            <Database size={20} className="me-2 text-primary" />
                                                            <h5 className="mb-0 fw-medium">Raw Survey Data</h5>
                                                        </div>
                                                        <Badge bg="secondary">{analysisResult.totalFields} records</Badge>
                                                    </div>
                                                    
                                                    <div className="glass-paper glass-paper--scroll" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                                        <pre style={{
                                                            margin: 0,
                                                            fontSize: '12px',
                                                            lineHeight: '1.4',
                                                            fontFamily: "'Fira Code', 'Courier New', monospace"
                                                        }}>
                                                            {JSON.stringify(analysisResult.rawData, null, 2)}
                                                        </pre>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </>
                                    ) : (
                                        <Card className="border-0 glass-surface glass-surface--thin">
                                            <Card.Body>
                                                <div className="d-flex align-items-center mb-3">
                                                    <Database size={20} className="me-2 text-primary" />
                                                    <h5 className="mb-0 fw-medium">Analysis Results</h5>
                                                </div>
                                                
                                                <div className="text-center py-5 glass-paper glass-paper--dashed">
                                                    <Database size={48} className="mb-3 opacity-50" />
                                                    <p className="mb-0">No irrigation data available. Please check the API endpoint connection.</p>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    )}
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default AnalysisComponent;